import React, { memo, useMemo } from 'react';
import { Alert, Avatar, Button, Icon, IconHighlight, Tag, Tooltip, Typography } from '@ann/web-components';
import {
  ASSIGNEE_TYPES,
  PERMISSION_TYPES,
  SHARE_CONFIRMATION_COPY,
} from '../../../../constants';
import { getAvatarSrc, getInitials } from '../../../../utils/avatarHelpers';
import { formatCombinedPermissionsSummary, normalizeShareAssignment } from '../../../../utils/permissionsHelpers';
import {
  BodyStack,
  CountItem,
  CountRow,
  DashboardTitleRow,
  FooterRow,
  NameBlock,
  Separator,
  Table,
  TableBody,
  TableHead,
  TdAccess,
  TdCanDo,
  TdName,
  TagsContainer,
  ThAccess,
  ThCanDo,
  ThName,
  Tr,
} from './ShareConfirmation.styled';

const SUMMARY_OPTIONS = { maxVisible: 3 };

const ShareConfirmation = ({
  onBack,
  onConfirm,
  assignments = [],
  dashboardName = '',
  confirming = false,
}) => {
  const normalizedRows = useMemo(
    () =>
      assignments.map((row) => ({
        row,
        normalized: normalizeShareAssignment(row),
      })),
    [assignments]
  );

  const { userCount, deptCount, variant } = useMemo(() => {
    const users = normalizedRows.filter(({ row }) => row?.type === ASSIGNEE_TYPES.USER);
    const depts = normalizedRows.filter(({ row }) => row?.type === ASSIGNEE_TYPES.DEPARTMENT);
    let v = 'both';
    if (users.length > 0 && depts.length === 0) v = 'users_only';
    if (depts.length > 0 && users.length === 0) v = 'depts_only';
    return { userCount: users.length, deptCount: depts.length, variant: v };
  }, [normalizedRows]);

  const displayName = `${dashboardName || ''}`.trim() || 'Dashboard';

  const renderCounts = () => {
    const u = (
      <CountItem key="u">
        <IconHighlight bgColor="Neutral" icon="person" iconColor="PrimaryAlt" size="small" />
        <Typography category="body" size="small" weight="medium" color="body">
          {SHARE_CONFIRMATION_COPY.userCount(userCount)}
        </Typography>
      </CountItem>
    );
    const d = (
      <CountItem key="d">
        <IconHighlight bgColor="Neutral" icon="work" iconColor="PrimaryAlt" size="small" />
        <Typography category="body" size="small" weight="medium" color="body">
          {SHARE_CONFIRMATION_COPY.departmentCount(deptCount)}
        </Typography>
      </CountItem>
    );
    if (variant === 'users_only') return u;
    if (variant === 'depts_only') return d;
    return (
      <>
        {u}
        <Separator aria-hidden>{SHARE_CONFIRMATION_COPY.separator}</Separator>
        {d}
      </>
    );
  };

  return (
    <BodyStack>
          <DashboardTitleRow>
            <Icon name="grid" size="small" color="neutral" />
            <Typography category="body" size="medium" weight="semibold" color="title">
              {displayName}
            </Typography>
          </DashboardTitleRow>

          <CountRow>{renderCounts()}</CountRow>

          <Typography category="subtitle" size="small" weight="semibold" color="subtitle">
            {SHARE_CONFIRMATION_COPY.recipientsSubtitle}
          </Typography>

          <Table>
            <TableHead>
              <ThName>
                <Typography category="label" size="small" weight="bold" color="body">
                  {SHARE_CONFIRMATION_COPY.tableHeaders.name}
                </Typography>
              </ThName>
              <ThCanDo>
                <Typography category="label" size="small" weight="bold" color="body">
                  {SHARE_CONFIRMATION_COPY.tableHeaders.canDo}
                </Typography>
              </ThCanDo>
              <ThAccess>
                <Typography category="label" size="small" weight="bold" color="body">
                  {SHARE_CONFIRMATION_COPY.tableHeaders.accessLevel}
                </Typography>
              </ThAccess>
            </TableHead>
            <TableBody>
              {normalizedRows.map(({ row, normalized }) => {
                const { permissionModes } = normalized;
                const isUser = row?.type === ASSIGNEE_TYPES.USER;
                const canDoTags = [];
                if (permissionModes.includes(PERMISSION_TYPES.VIEW)) {
                  canDoTags.push({
                    key: PERMISSION_TYPES.VIEW,
                    label: SHARE_CONFIRMATION_COPY.accessView,
                    color: 'neutral',
                  });
                }
                if (permissionModes.includes(PERMISSION_TYPES.EDIT)) {
                  canDoTags.push({
                    key: PERMISSION_TYPES.EDIT,
                    label: SHARE_CONFIRMATION_COPY.accessEdit,
                    color: 'informative',
                  });
                }
                if (permissionModes.includes(PERMISSION_TYPES.CREATE)) {
                  canDoTags.push({
                    key: PERMISSION_TYPES.CREATE,
                    label: SHARE_CONFIRMATION_COPY.accessCreate,
                    color: 'neutral',
                  });
                }
                const { text: summary, fullList } = formatCombinedPermissionsSummary(
                  normalized,
                  SUMMARY_OPTIONS
                );
                const hasMore = summary.includes('+');
                const visibleTags = canDoTags.slice(0, 3);
                const hiddenTags = canDoTags.length - visibleTags.length;

                return (
                  <Tr key={row.id}>
                    <TdName>
                      {isUser ? (
                        <Avatar
                          width={20}
                          height={19}
                          alt=""
                          label={getInitials(row?.name)}
                          src={getAvatarSrc(row?.name, row?.id)}
                        />
                      ) : (
                        <Icon
                          name="work"
                          size="custom"
                          width="20px"
                          height="15px"
                          color="primary-alt"
                        />
                      )}
                      <NameBlock>
                        <Typography category="body" size="small" weight="semibold" color="title" titleOnHover>
                          {row?.name}
                        </Typography>
                      </NameBlock>
                    </TdName>
                    <TdCanDo>
                      <TagsContainer>
                        {visibleTags.map((tag) => (
                          <Tag key={`${row.id}-${tag.key}`} size="small" color={tag.color} text={tag.label} />
                        ))}
                        {hiddenTags > 0 && (
                          <Tag
                            key={`${row.id}-more-tags`}
                            size="small"
                            color="neutral"
                            text={`+${hiddenTags}`}
                          />
                        )}
                      </TagsContainer>
                    </TdCanDo>
                    <TdAccess>
                      {hasMore ? (
                        <Tooltip
                          content={
                            <div>
                              {fullList.map((item) => (
                                <div key={`${row.id}-${item}`}>{`• ${item}`}</div>
                              ))}
                            </div>
                          }
                          position="top"
                          arrow={false}
                        >
                          <Typography category="body" size="small" weight="regular" color="body">
                            {summary}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography category="body" size="small" weight="regular" color="body">
                          {summary}
                        </Typography>
                      )}
                    </TdAccess>
                  </Tr>
                );
              })}
            </TableBody>
          </Table>

          <Alert
            title={SHARE_CONFIRMATION_COPY.warningTitle}
            description={SHARE_CONFIRMATION_COPY.warningBody}
            severity="warning"
            closable={false}
            autoClose={false}
            position="relative"
            className="share-confirmation-alert"
            maxWidth="100%"
            noShadow={true}
          />

          <FooterRow>
            <Button
              type="secondary"
              size="medium"
              label={SHARE_CONFIRMATION_COPY.backButton}
              disabled={confirming}
              onClick={onBack}
            />
            <Button
              type="primary"
              size="medium"
              label={SHARE_CONFIRMATION_COPY.confirmButton}
              disabled={confirming || assignments.length === 0}
              onClick={onConfirm}
            />
          </FooterRow>
    </BodyStack>
  );
};

export default memo(ShareConfirmation);
